import {FC, useState} from 'react';
import {api, ESummaryStatus, useT} from '@kcast-fe/core';
import {Box, Center, Flex, Progress, Stack} from '@chakra-ui/react';
import {Switch, SummaryBadge, Loader, ConfirmDialog} from 'ui-kit';

interface IProps {
  id: string;
}

const SummaryStateTabFC: FC<IProps> = ({id}) => {
  const {data: state, isLoading} = api.summary.useSummaryStateQuery(id);
  const {data: summarySettings, isLoading: isSettings} =
    api.summary.useSummaryPromptSettingsQuery(id);
  const {data: settings} = api.settings.useSettingsQuery();
  const {data: list} = api.summary.useSummaryListQuery();

  const {t} = useT();

  const replayingProgress = list?.find((item) => item.id === id)?.replaying_progress;

  const updateStateMutation = api.summary.useUpdateSummaryStateMutation();
  const replayMutation = api.summary.useReplaySummaryMutation();
  const cancelMutation = api.summary.useCancelReplaySummaryMutation();

  const [confirmReplay, setConfirmReplay] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const handleReplay = () => {
    replayMutation.mutate(id, {onSuccess: () => setConfirmReplay(false)});
  };

  const handleCancel = () => {
    cancelMutation.mutate(id, {onSuccess: () => setConfirmCancel(false)});
  };

  if (isLoading || isSettings || !state) {
    return (
      <Center h={'100px'}>
        <Loader size={'lg'} />
      </Center>
    );
  }

  const updateEvery = summarySettings?.update_every_n_turns ?? 1;
  const turns = state.turns_since_last_update;
  const progress = Math.min((turns / updateEvery) * 100, 100);

  return (
    <>
      <Stack gap={'20px'} p={'4px 0'}>
        <Flex alignItems={'center'} gap={'12px'}>
          <Box color={'{colors.textSecondary}'} minW={'80px'}>
            {t('summary.fields.status')}
          </Box>
          <Box w={state.status === ESummaryStatus.Replaying ? '30%' : 'initial'}>
            <SummaryBadge status={state.status} replaying_progress={replayingProgress} />
          </Box>
          {state.status === ESummaryStatus.Replaying && (
            <Box
              as={'button'}
              color={'{colors.textTertiary}'}
              textDecoration={'underline'}
              cursor={'pointer'}
              onClick={() => setConfirmCancel(true)}
            >
              {t('summary.state.cancel')}
            </Box>
          )}
          {state.status === ESummaryStatus.Failed && (
            <Box
              as={'button'}
              color={'{colors.stateError}'}
              textDecoration={'underline'}
              cursor={'pointer'}
              onClick={() => setConfirmReplay(true)}
            >
              {t('summary.actions.retryReplay')}
            </Box>
          )}
        </Flex>

        <Flex alignItems={'center'} gap={'12px'}>
          <Box color={'{colors.textSecondary}'} minW={'80px'}>
            {t('summary.fields.enabled')}
          </Box>
          <Switch
            checked={state.enabled}
            onChange={(checked) => updateStateMutation.mutate({id, data: {enabled: checked}})}
            disabled={updateStateMutation.isPending}
          />
          <Box color={'{colors.textTertiary}'}>
            {state.enabled
              ? t('summary.state.enabledDescription', {name: settings?.assistant?.name})
              : t('summary.state.disabledDescription')}
          </Box>
        </Flex>

        <Box>
          <Flex alignItems={'center'} gap={'12px'} mb={'8px'}>
            <Box color={'{colors.textSecondary}'} minW={'80px'}>
              {t('summary.fields.progress')}
            </Box>
            <Box color={'{colors.textPrimary)'}>
              {t('summary.state.turnsSinceUpdate', {turns, updateEvery})}
            </Box>
          </Flex>
          <Progress.Root value={progress} size={'sm'} borderRadius={'full'}>
            <Progress.Track>
              <Progress.Range />
            </Progress.Track>
          </Progress.Root>
          <Box color={'{colors.textTertiary}'} mt={'4px'}>
            {t('summary.state.autoUpdateEvery', {updateEvery})}
          </Box>
        </Box>
      </Stack>

      <ConfirmDialog
        isOpen={confirmReplay}
        title={t('summary.confirm.rebuildTitle')}
        body={t('summary.confirm.replayBody')}
        confirmLabel={t('summary.actions.replay')}
        isLoading={replayMutation.isPending}
        onConfirm={handleReplay}
        onCancel={() => setConfirmReplay(false)}
      />

      <ConfirmDialog
        isOpen={confirmCancel}
        title={t('summary.confirm.cancelReplayTitle')}
        body={t('summary.confirm.cancelReplayBody')}
        confirmLabel={t('summary.actions.cancelReplay')}
        isLoading={cancelMutation.isPending}
        onConfirm={handleCancel}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
};

export const SummaryStateTab = SummaryStateTabFC;
